"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";

// Inline SVG for Apple logo (brand mark)
const AppleLogo = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M11.182.008C11.148-.03 9.923.023 8.857 1.18c-1.066 1.156-.902 2.482-.878 2.516s1.52.087 2.475-1.258.762-2.391.728-2.43m3.314 11.733c-.048-.096-2.325-1.234-2.113-3.422s1.675-2.789 1.698-2.854-.597-.79-1.254-1.157a3.7 3.7 0 0 0-1.563-.434c-.108-.003-.483-.095-1.254.116-.508.139-1.653.589-1.968.607-.316.018-1.256-.522-2.267-.665-.647-.125-1.333.131-1.824.328-.49.196-1.422.754-2.074 2.237-.652 1.482-.311 3.83-.067 4.56s.625 1.924 1.273 2.796c.576.984 1.34 1.667 1.659 1.899s1.219.386 1.843.067c.502-.308 1.408-.485 1.766-.472.357.013 1.061.154 1.782.539.571.197 1.111.115 1.652-.105.541-.221 1.324-1.059 2.238-2.758q.52-1.185.473-1.282"/>
  </svg>
);

// Inline SVG for Google Play logo
const GooglePlayLogo = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 512 512"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M48 59.49v393a4.33 4.33 0 007.37 3.07L260 256 55.37 56.42A4.33 4.33 0 0048 59.49zM345.8 174L89.22 32.64l-.16-.09c-4.42-2.4-8.62 3.58-5 7L285 256 345.8 174zM84.08 472.39c-3.64 3.42.56 9.4 5 7l.16-.09L345.8 338 285 256 84.08 472.39zM448.83 234.35l-61.64-35.53L325.3 256l62 62 61.64-35.53a24.45 24.45 0 000-48.12z" />
  </svg>
);

const screenshots = [
  { id: 0, src: "/appscreenshots/app-home.webp", alt: "Home Screen" },
  { id: 1, src: "/appscreenshots/app-live.webp", alt: "Live Screen" },
  { id: 2, src: "/appscreenshots/app-more.webp", alt: "More Screen" },
  { id: 3, src: "/appscreenshots/app-community.webp", alt: "Community Screen" },
  { id: 4, src: "/appscreenshots/app-bible.webp", alt: "Bible Screen" },
];

type AppDownloadTranslations = {
  eyebrow: string;
  title: string;
  titleHighlight: string;
  description: string;
  downloadOnThe: string;
  appStore: string;
  getItOn: string;
  googlePlay: string;
  rating: string;
  freeForever: string;
  platforms: string;
};

const DEFAULT_APP_DOWNLOAD_TRANSLATIONS: AppDownloadTranslations = {
  eyebrow: "Mobile App",
  title: "Take YAF",
  titleHighlight: "Everywhere",
  description:
    "Your daily Bible plan, live services, prayer journal, and AI-powered scripture search — all in your pocket.",
  downloadOnThe: "Download on the",
  appStore: "App Store",
  getItOn: "Get it on",
  googlePlay: "Google Play",
  rating: "5.0 Rating",
  freeForever: "Free Forever",
  platforms: "iOS & Android",
};

type AppDownloadSectionProps = {
  translations?: AppDownloadTranslations;
};

export const AppDownloadSection = ({ translations }: AppDownloadSectionProps = {}) => {
  const t = translations ?? DEFAULT_APP_DOWNLOAD_TRANSLATIONS;
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % screenshots.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  const getPosition = (index: number) => {
    const relativeIndex = (index - currentIndex + 5) % 5;
    
    switch (relativeIndex) {
      case 0: // Center
        return { x: "calc(-50% + 0%)", y: "calc(-50% + 0%)", scale: 1, rotate: 0, zIndex: 30, filter: "brightness(1)" };
      case 1: // Mid Right
        return { x: "calc(-50% + 55%)", y: "calc(-50% + 5%)", scale: 0.85, rotate: 7, zIndex: 20, filter: "brightness(0.9)" };
      case 2: // Far Right
        return { x: "calc(-50% + 90%)", y: "calc(-50% + 15%)", scale: 0.7, rotate: 14, zIndex: 10, filter: "brightness(0.8)" };
      case 3: // Far Left
        return { x: "calc(-50% - 90%)", y: "calc(-50% + 15%)", scale: 0.7, rotate: -14, zIndex: 10, filter: "brightness(0.8)" };
      case 4: // Mid Left
        return { x: "calc(-50% - 55%)", y: "calc(-50% + 5%)", scale: 0.85, rotate: -7, zIndex: 20, filter: "brightness(0.9)" };
      default:
        return { x: "-50%", y: "-50%", scale: 0, rotate: 0, zIndex: 0 };
    }
  };

  return (
    <section className="relative bg-gradient-to-b from-stone-50 to-white py-24 lg:py-32 px-6 overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 2px 2px, #000 1px, transparent 0)`,
        backgroundSize: '48px 48px'
      }} />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">

          {/* LEFT: Text & CTAs */}
          <div className="order-2 lg:order-1">
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-brand-red font-black uppercase tracking-[0.4em] text-[11px] mb-6 block"
            >
              {t.eyebrow}
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-serif text-5xl lg:text-7xl font-bold leading-[0.95] tracking-tight text-stone-900 mb-6"
            >
              {t.title}
              <br />
              <span className="italic text-brand-red">{t.titleHighlight}</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-stone-600 text-lg lg:text-xl font-normal leading-relaxed mb-8 max-w-lg"
            >
              {t.description}
            </motion.p>

            {/* Download buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              {/* App Store */}
              <Link
                href="https://apps.apple.com/us/app/youth-alive-canada/id6758669107"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 bg-stone-900 text-white px-6 py-3.5 rounded-xl font-bold transition-all duration-300 hover:bg-brand-red hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <AppleLogo className="w-5 h-5 shrink-0 transition-colors" />
                <div className="flex flex-col text-left leading-tight">
                  <span className="text-[8px] font-black uppercase tracking-[0.15em] opacity-70">
                    {t.downloadOnThe}
                  </span>
                  <span className="text-sm font-black tracking-tight">{t.appStore}</span>
                </div>
              </Link>

              {/* Google Play */}
              <Link
                href="https://play.google.com/store/apps/details?id=com.yafcanada.app"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 bg-white text-stone-900 px-6 py-3.5 rounded-xl font-bold border-2 border-stone-200 transition-all duration-300 hover:border-brand-red hover:text-brand-red hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <GooglePlayLogo className="w-5 h-5 shrink-0 transition-colors" />
                <div className="flex flex-col text-left leading-tight">
                  <span className="text-[8px] font-black uppercase tracking-[0.15em] opacity-70">
                    {t.getItOn}
                  </span>
                  <span className="text-sm font-black tracking-tight">{t.googlePlay}</span>
                </div>
              </Link>
            </motion.div>

            {/* Trust badges */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex items-center gap-6 mt-8"
            >
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-4 h-4 text-brand-gold" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-xs font-semibold text-stone-600">{t.rating}</span>
              </div>
              <div className="text-xs font-semibold text-stone-400">|</div>
              <span className="text-xs font-semibold text-stone-600">{t.freeForever}</span>
              <div className="text-xs font-semibold text-stone-400">|</div>
              <span className="text-xs font-semibold text-stone-600">{t.platforms}</span>
            </motion.div>
          </div>

          {/* RIGHT: App screenshots rotating layout */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative order-1 lg:order-2 flex items-center justify-center h-[450px] sm:h-[550px] lg:h-[700px] w-full mt-12 lg:mt-0"
          >
            <div className="relative w-full max-w-[600px] h-full flex items-center justify-center">
              {screenshots.map((screen, index) => (
                <motion.div
                  key={screen.id}
                  animate={getPosition(index)}
                  transition={{ duration: 0.8, type: "spring", bounce: 0.2 }}
                  className="absolute left-1/2 top-1/2 w-[170px] sm:w-[220px] lg:w-[300px] drop-shadow-[0_20px_40px_rgba(0,0,0,0.3)] cursor-pointer"
                  style={{ originX: 0.5, originY: 0.5 }}
                  onClick={() => setCurrentIndex(index)}
                >
                  <Image
                    src={screen.src}
                    alt={screen.alt}
                    width={300}
                    height={620}
                    className="w-full h-auto"
                    unoptimized
                  />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
